import moment from "moment";

import { Client } from "@/Common/Api/Client";
import { ExternalEmployeeResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/ExternalEmployeeResponse";
import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExternalPatientResponse } from "@/Communications/Api/Dto/Responses/ExternalPatientResponse";
import { MessageResponse } from "@/Communications/Api/Dto/Responses/MessageResponse";
import { MessageInfo } from "@/Communications/Models/MessageInfo";
import { ExportableFileHelper } from "@/ExportableFile/Helpers/ExportableFileHelper";
import { BaseExternalMessageFormComponent, BaseExternalMessageFormState, IBaseExternalMessageFormProps } from "./BaseExternalMessageFormComponent";

enum ExternalMessageFormContext {
    Draft,
    Resend,
    Forward,
    Reply,
    ReplyAll
}

interface IExternalMessageFormProps extends IBaseExternalMessageFormProps {
    messageId?: string;
    context?: ExternalMessageFormContext;
    cacheState?: BaseExternalMessageFormState;
}

class ExternalMessageFormComponent extends BaseExternalMessageFormComponent<IExternalMessageFormProps, BaseExternalMessageFormState> {
    public componentDidMount(): void {
        this.setStateFromProps(this.props);
    }

    public componentDidUpdate(prevProps: IExternalMessageFormProps): void {
        if (
            this.props.messageId !== prevProps.messageId ||
            this.props.context !== prevProps.context ||
            this.props.cacheState !== prevProps.cacheState
        ) {
            this.setStateFromProps(this.props);
        }
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        if (!this.state)
            return;

        communicationsStore.saveExternalMessageFormState(this.state);
        if (this.state.shouldClearCacheState)
            communicationsStore.clearExternalMessageFormState();
    }

    public shouldComponentUpdate(nextProps: IExternalMessageFormProps, nextState: BaseExternalMessageFormState): boolean {
        return (this.props.messageId != nextProps.messageId
            || this.props.context != nextProps.context
            || this.props.cacheState != nextProps.cacheState)
            || (this.state != nextState);
    }

    public render(): JSX.Element {
        return (
            <div className="workspace">
                {super.render()}
            </div>
        );
    }

    protected renderEmpty(): JSX.Element {
        return (
            <div>
                <div className="myle-page-header bottom-border">
                    <div className="row">
                        <div className="col-xs-12">
                            <p className="header-font-color" style={{ minHeight: "60px" }}>
                                {cacheStore.GetString("ExternalCommunicationsLabel")}
                            </p>
                        </div>
                    </div>
                </div>
                <div className="fxheight hide-overflow" style={{ minHeight: "90vh" }} />
            </div>
        );
    }

    protected renderHeader(): JSX.Element {
        return (
            <div className="myle-page-header bottom-border">
                <div className="row" style={{ paddingLeft: "30px" }}>
                    {super.renderHeader()}
                </div>
            </div>
        );
    }

    protected renderBody(): JSX.Element {
        return (
            <div style={{ padding: "40px 20% 0 5%", height: "79.6vh", overflow: "hidden auto" }}>
                {super.renderBody()}
            </div>
        );
    }

    protected renderFooter(): JSX.Element {
        return (
            <div className="ws-footer">
                <div className="col-lg-12 col-md-12 col-sm-12 text-right">
                    <div style={{ float: "left" }}>{this.renderRefusalReason()}</div>
                    <ButtonControl
                        className="btn btn-default push-right-5"
                        label={cacheStore.GetString("CancelLabel")}
                        onClick={() => this.mutateState(s => s.shouldClearCacheState = true, () => this.props.onClose && this.props.onClose())}
                    />
                    <ButtonControl
                        className="btn btn-primary"
                        label={cacheStore.GetString(this.props.context && this.props.context === ExternalMessageFormContext.Resend ? "ResendLabel" : "SendLabel")}
                        disabled={!this.isFormValid || this.state.isSending}
                        onClick={() => this.mutateState(s => s.isSending = true, () => this.handleSend())}
                    />
                </div>
            </div>
        );
    }

    private setStateFromProps(props: IExternalMessageFormProps): void {
        if (props.cacheState) {
            this.setState(props.cacheState);
            return;
        }

        if (props.messageId)
            this.load(props.messageId);
        else {
            let state = BaseExternalMessageFormState.createDefault();
            this.setState(state);
        }
    }

    private load(id: string): void {
        if (!id)
            return;

        let requestData = {
            ExternalMessageId: id,
            ShouldLoadPatientInfoForBanner: true
        };

        Client.Postv2(
            ServerOperations.Server.Myle_ExternalCommunication_LoadMessage,
            requestData,
            response => {
                let messageInfo = MessageInfo.fromResponse(response.MessageInfo);
                if (messageInfo)
                    this.setState(this.buildStateFromResponse(messageInfo, this.props.context));
            }
        );
    }

    private buildStateFromResponse(messageInfo: MessageInfo, context: ExternalMessageFormContext): BaseExternalMessageFormState {
        let state = new BaseExternalMessageFormState();
        state.selectedAttachments = messageInfo.attachments
            ? messageInfo.attachments.map(x => ExportableFileHelper.fromAttachedObject(x).toExportableFileModel())
            : [];
        state.generatedAttachment = messageInfo.generatedAttachment;
        state.messageModel = messageInfo.message;
        state.messageModel.PatientProfileId = null;
        state.recipients = messageInfo.recipients ? messageInfo.recipients.map(x => ExternalEmployeeResponse.fromResponse(x.Employee)) : [];
        if (messageInfo.patient || messageInfo.internalPatient) {
            state.selectedPatient = messageInfo.internalPatient
                ? InternalPatientResponse.fromResponse(messageInfo.internalPatient)
                : ExternalPatientResponse.fromResponse(messageInfo.patient);
        }

        switch (context) {
            case ExternalMessageFormContext.Draft:
            case ExternalMessageFormContext.Resend:
                state.refusalReasonLabel = messageInfo.recipients && messageInfo.recipients.firstOrDefault() && messageInfo.recipients.firstOrDefault().TranslatableRefusalReason
                    ? messageInfo.recipients.firstOrDefault().TranslatableRefusalReason.Label
                    : null;
                return state;
            case ExternalMessageFormContext.Forward:
                state.recipients = [];
                break;
            case ExternalMessageFormContext.Reply:
                state.recipients = [ExternalEmployeeResponse.fromResponse(messageInfo.fromEmployee)];
                break;
            case ExternalMessageFormContext.ReplyAll:
                // TODO
                break;
        }
        state.messageModel.Id = GuidHelper.Empty();
        state.messageModel.Body = this.buildBodyForForwardOrReply(messageInfo.message, messageInfo.fromEmployee);
        state.shouldIncludeSearchFromSameClient = true;
        return state;
    }

    private buildBodyForForwardOrReply(message: MessageResponse, fromEmployee: ExternalEmployeeResponse): string {
        const sendDate = moment.utc(message.SendDate).local();
        const formattedDate = sendDate.format("YYYY/MM/DD") + " " + cacheStore.GetString("AtLabel") + " " + sendDate.format("hh:mm:ss");
        const fromContactPersonName = cacheStore.GetCliniciansPersonsByContactFacetId(fromEmployee.EmployeeId)
            ? cacheStore.GetCliniciansPersonsByContactFacetId(fromEmployee.EmployeeId).Name
            : `${fromEmployee.LastName}, ${fromEmployee.FirstName}`;

        return `\n\n---------------------------------------------\n${formattedDate}     ${fromContactPersonName}\nSubject: ${message.Subject || ""}\n\n${message.Body || ""}`;
    }

    private renderRefusalReason(): JSX.Element {
        if (!this.state.refusalReasonLabel)
            return null;

        return (
            <div style={{ color: "red", marginTop: "1vh" }}>
                {cacheStore.GetString(this.state.refusalReasonLabel)}
            </div>
        );
    }
}

export { ExternalMessageFormComponent, ExternalMessageFormContext };
